import React, { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

interface ButtonProps {
  className?: string;
  label?: string;
  text?: string; // Add text prop for compatibility
  isLoading?: boolean;
  disabled?: boolean;
  onClick: (event: React.FormEvent) => void;
  children?: React.ReactNode; // To accept children elements (icons or other JSX)
  shortcutTip?: string;
  shortcutKeys?: string[];
  type?: 'button' | 'submit' | 'reset'; // Add type prop
  tooltipPlacement?: 'top' | 'bottom'; // 'top' needs headroom above the button — use 'bottom' when it sits flush against the viewport top (e.g. the page toolbar)
}

export const keyMap = {
  SAVE: 'ctrl+s', // Define a shortcut key (Ctrl+S)
  NEW: 'alt+n',   // Define another shortcut key (Alt+N)
};

const LoadingButton: React.FC<ButtonProps> = ({
  label,
  text, // Add text prop
  className,
  isLoading,
  disabled,
  onClick,
  children,
  shortcutTip,
  shortcutKeys,
  type = 'button', // Default to button type
  tooltipPlacement = 'top',
}) => {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Use text prop if provided, otherwise fallback to label for backward compatibility
  const buttonText = text || label || 'Submit'.toTranslatedDisplay();

  const handleClick = (event: React.FormEvent) => {
    if (btnRef.current) {
      btnRef.current.setAttribute('data-kt-indicator', 'on');
    }

    onClick(event);

    setTimeout(() => {
      if (btnRef.current) {
        btnRef.current.removeAttribute('data-kt-indicator');
      }
    }, 3000); // Disable indicator after 3 seconds
  };

  const handleMouseEnter = () => setShowTooltip(true);
  const handleMouseLeave = () => setShowTooltip(false);

  const handleKeyboardShortcut = (event: KeyboardEvent) => {
    event.stopPropagation()
    event.preventDefault(); // Prevent default browser behavior (e.g., Save Page on Ctrl+S)
    event.stopPropagation()
    if (btnRef.current) {
      btnRef.current.click(); // Programmatically trigger a button click
    }
  };

  useHotkeys(
    shortcutKeys || [],
    handleKeyboardShortcut,
    { enableOnFormTags: ['input', 'textarea', 'select'] } // Enable shortcut in form fields
  );

  useEffect(() => {
    if (!isLoading && btnRef.current) {
      btnRef.current.removeAttribute('data-kt-indicator');
    }
  }, [isLoading]);

  return (
    <span className="position-relative d-inline-block">
      <button
        ref={btnRef}
        disabled={disabled || isLoading}
        onClick={(e) => handleClick(e)}
        type={type}
        className={`btn btn-primary ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children ? (
          <>
            {isLoading &&
              <span className="spinner-border spinner-border-sm align-middle mx-2"></span>
            }
            {children}
          </>
        ) : (
          <>
            {isLoading &&
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            }
            <span className="indicator-label">{buttonText}</span>
          </>
        )}
      </button>
      {(showTooltip && shortcutTip) && (
        <div
          className={`tooltip fade show bs-tooltip-${tooltipPlacement}`}
          role="tooltip"
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            ...(tooltipPlacement === 'bottom'
              ? { top: '100%', marginTop: '0.5rem' }
              : { bottom: '100%', marginBottom: '0.5rem' }),
            zIndex: 1080,
            whiteSpace: 'nowrap',
          }}
        >
          <div className="tooltip-arrow"></div>
          <div className="tooltip-inner">{shortcutTip}</div>
        </div>
      )}
    </span>
  );
};

export default LoadingButton;